// ============================================
// FILE: /frontend/src/components/admin/ChamaInterestsPanel.jsx
// PURPOSE: Admin panel for managing shares interests
// FIXED: Added buttons for PENDING status, improved workflow
// ============================================

import { useState, useEffect } from 'react';
import { Users, Loader2, Filter, Search, Mail, Phone, CheckCircle, XCircle, AlertCircle, TrendingUp, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../../hooks/useToast';
import { chamaAPI } from '../../api/chama.api';
import ListingImage from '../ListingImage';

export default function ChamaInterestsPanel() {
  const { toast } = useToast();
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchInterests();
  }, [currentPage, statusFilter]);

  const fetchInterests = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await chamaAPI.getAllChamaInterests(params);
      setInterests(response.interests);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch interests:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load chama interests.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Mark deposit as paid (PENDING -> DEPOSIT_PAID)
  const handleMarkDepositPaid = async () => {
    if (!selectedInterest) return;
    setUpdating(true);
    try {
      await chamaAPI.updateChamaInterestStatus(
        selectedInterest.id,
        'DEPOSIT_PAID',
        adminNotes
      );

      setInterests(
        interests.map((i) =>
          i.id === selectedInterest.id
            ? { ...i, status: 'DEPOSIT_PAID', adminNotes }
            : i
        )
      );

      toast({
        title: 'Deposit Marked as Paid',
        description: 'Status updated to Deposit Paid. You can now approve or decline.',
      });

      setSelectedInterest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Failed to mark deposit paid:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update status.',
      });
    } finally {
      setUpdating(false);
    }
  };

  // Decline interest (any status -> DECLINED)
  const handleDecline = async () => {
    if (!selectedInterest) return;
    setUpdating(true);
    try {
      await chamaAPI.updateChamaInterestStatus(
        selectedInterest.id,
        'DECLINED',
        adminNotes
      );

      setInterests(
        interests.map((i) =>
          i.id === selectedInterest.id
            ? { ...i, status: 'DECLINED', adminNotes }
            : i
        )
      );

      toast({
        title: 'Interest Declined',
        description: 'The shares interest has been declined.',
      });

      setSelectedInterest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Failed to decline:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to decline interest.',
      });
    } finally {
      setUpdating(false);
    }
  };

  // Approve deposit and reserve shares (DEPOSIT_PAID -> CONFIRMED)
  const handleApprove = async () => {
    if (!selectedInterest) return;
    setUpdating(true);
    try {
      await chamaAPI.approveDeposit(
        selectedInterest.id,
        true, // approved
        adminNotes
      );

      setInterests(
        interests.map((i) =>
          i.id === selectedInterest.id
            ? { 
                ...i, 
                status: 'CONFIRMED',
                depositPaid: true,
                adminNotes 
              }
            : i
        )
      );

      toast({
        title: 'Interest Approved',
        description: 'Shares have been reserved for this buyer.',
      });

      setSelectedInterest(null);
      setAdminNotes('');
      
      // Refresh to update shares sold count
      fetchInterests();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to approve interest.',
      });
    } finally {
      setUpdating(false);
    }
  };

  // Save admin notes without changing status
  const handleSaveNotes = async () => {
    if (!selectedInterest) return;
    setUpdating(true);
    try {
      await chamaAPI.updateChamaInterestStatus(
        selectedInterest.id,
        selectedInterest.status, // keep current status
        adminNotes
      );

      setInterests(
        interests.map((i) =>
          i.id === selectedInterest.id
            ? { ...i, adminNotes }
            : i
        )
      );

      toast({
        title: 'Notes Saved',
        description: 'Admin notes have been updated.',
      });
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save notes.',
      });
    } finally {
      setUpdating(false);
    }
  };

  const filteredInterests = interests.filter((interest) =>
    interest.listing.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    interest.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    interest.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const colors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      DEPOSIT_PAID: 'bg-blue-100 text-blue-800',
      CONFIRMED: 'bg-green-100 text-green-800',
      DECLINED: 'bg-red-100 text-red-800',
      REFUNDED: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <AlertCircle className="w-4 h-4" />;
      case 'DEPOSIT_PAID':
        return <TrendingUp className="w-4 h-4" />;
      case 'CONFIRMED':
        return <CheckCircle className="w-4 h-4" />;
      case 'DECLINED':
      case 'REFUNDED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (loading && interests.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            Chama Shares Management
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Pending</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-700">
                  {interests.filter((i) => i.status === 'PENDING').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Deposit Paid</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-700">
                  {interests.filter((i) => i.status === 'DEPOSIT_PAID').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Confirmed</p>
                <p className="text-xl sm:text-2xl font-bold text-green-700">
                  {interests.filter((i) => i.status === 'CONFIRMED').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Declined</p>
                <p className="text-xl sm:text-2xl font-bold text-red-700">
                  {interests.filter((i) => i.status === 'DECLINED').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Refunded</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-700">
                  {interests.filter((i) => i.status === 'REFUNDED').length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by listing, name, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 sm:h-10 text-xs sm:text-sm"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 h-9 sm:h-10 text-xs sm:text-sm">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="DEPOSIT_PAID">Deposit Paid</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="DECLINED">Declined</SelectItem>
                <SelectItem value="REFUNDED">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Interests List */}
      <Card>
        <CardContent className="p-0">
          {filteredInterests.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No chama interests found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredInterests.map((interest) => (
                <div
                  key={interest.id}
                  className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedInterest(interest);
                    setAdminNotes(interest.adminNotes || '');
                  }}
                >
                  <div className="flex flex-col lg:flex-row gap-3">
                    {/* Listing Info */}


                    <div className="flex gap-3 flex-1">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded flex-shrink-0 overflow-hidden">
                        <ListingImage
                          src={interest.listing.imageUrl}
                          alt={interest.listing.title}
                          type={interest.listing.type}
                          className="w-full h-full object-cover"
                        />
                      </div>


                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm sm:text-base line-clamp-1">
                          {interest.listing.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-primary font-semibold">
                          KES {parseFloat(interest.listing.price).toLocaleString()}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          <Badge variant="outline" className="text-[10px] sm:text-xs">
                            {interest.sharesWanted} shares
                          </Badge>
                          <Badge variant="outline" className="text-[10px] sm:text-xs">
                            KES {parseFloat(interest.totalAmount).toLocaleString()}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <p className="font-semibold text-xs sm:text-sm">{interest.fullName}</p>
                      <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-muted-foreground mt-1">
                        <Mail className="w-3 h-3" />
                        {interest.email}
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                        <Phone className="w-3 h-3" />
                        {interest.phone}
                      </div>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        {new Date(interest.createdAt).toLocaleString()}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="flex flex-col items-start lg:items-end gap-1">
                      <Badge className={`${getStatusBadge(interest.status)} text-xs px-2 py-1 flex items-center gap-1`}>
                        {getStatusIcon(interest.status)}
                        {interest.status.replace('_', ' ')}
                      </Badge>
                      {interest.depositPaid && (
                        <Badge variant="outline" className="text-[10px] text-green-700 border-green-300">
                          Deposit Verified
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Deposit Amount */}
                  <div className="mt-3 bg-purple-50 rounded-lg p-2 sm:p-3 border border-purple-200">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-purple-700">Total Amount: </span>
                        <span className="font-semibold">
                          KES {parseFloat(interest.totalAmount).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-purple-700">Deposit (10%): </span>
                        <span className="font-semibold text-purple-900">
                          KES {parseFloat(interest.depositRequired).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="h-8 sm:h-9 text-xs sm:text-sm"
          >
            Previous
          </Button>
          <span className="flex items-center px-3 text-xs sm:text-sm">
            Page {currentPage} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === pagination.pages}
            className="h-8 sm:h-9 text-xs sm:text-sm"
          >
            Next
          </Button>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedInterest} onOpenChange={() => setSelectedInterest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg md:text-xl">
              Review Shares Interest
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {selectedInterest?.status === 'PENDING' && 'Verify payment and mark deposit as paid or decline'}
              {selectedInterest?.status === 'DEPOSIT_PAID' && 'Approve to reserve shares or decline this request'}
              {(selectedInterest?.status === 'CONFIRMED' || selectedInterest?.status === 'DECLINED') && 'View interest details'}
            </DialogDescription>
          </DialogHeader>

          {selectedInterest && (
            <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
              {/* Listing Info */}
              <Card className="bg-gray-50">
                <CardContent className="p-3 sm:p-4">
                  <h4 className="font-semibold text-sm sm:text-base mb-2">
                    {selectedInterest.listing.title}
                  </h4>
                  <p className="text-primary font-bold text-base sm:text-lg">
                    KES {parseFloat(selectedInterest.listing.price).toLocaleString()}
                  </p>
                  <div className="mt-2 text-xs sm:text-sm text-muted-foreground">
                    <p>Total Shares: {selectedInterest.listing.totalShares}</p>
                    <p>Sold: {selectedInterest.listing.sharesSold}</p>
                  </div>
                </CardContent>
              </Card>

              {/* User Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                  <p className="font-semibold text-sm">{selectedInterest.fullName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Email</p>
                  <p className="font-semibold text-sm break-all">{selectedInterest.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Phone</p>
                  <p className="font-semibold text-sm">{selectedInterest.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                  <Badge className={`${getStatusBadge(selectedInterest.status)} text-xs`}>
                    {selectedInterest.status.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              {/* Shares Details */}
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-3 sm:p-4">
                  <h5 className="font-semibold text-xs sm:text-sm mb-2">Shares Purchase</h5>
                  <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                    <div>
                      <p className="text-purple-700">Shares Wanted</p>
                      <p className="font-semibold text-lg">{selectedInterest.sharesWanted}</p>
                    </div>
                    <div>
                      <p className="text-purple-700">Share Price</p>
                      <p className="font-semibold">
                        KES {parseFloat(selectedInterest.sharePricePaid).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-purple-700">Total Amount</p>
                      <p className="font-semibold text-purple-900">
                        KES {parseFloat(selectedInterest.totalAmount).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-purple-700">Deposit Required (10%)</p>
                      <p className="font-semibold text-purple-900">
                        KES {parseFloat(selectedInterest.depositRequired).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Notes */}
              {selectedInterest.notes && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">User Notes:</p>
                  <Card>
                    <CardContent className="p-3 text-xs sm:text-sm">
                      {selectedInterest.notes}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Admin Notes */}
              <div className="space-y-2">
                <label className="text-xs sm:text-sm font-semibold">Admin Notes</label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about deposit verification, phone call details, etc..."
                  rows={4}
                  className="text-xs sm:text-sm resize-none"
                  disabled={selectedInterest.status === 'CONFIRMED' || selectedInterest.status === 'DECLINED'}
                />
                {(selectedInterest.status === 'CONFIRMED' || selectedInterest.status === 'DECLINED') && (
                  <p className="text-xs text-muted-foreground">Notes are locked for completed interests</p>
                )}
              </div>

              {/* ACTION BUTTONS - PENDING STATUS */}
              {selectedInterest.status === 'PENDING' && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      onClick={handleMarkDepositPaid}
                      disabled={updating}
                      className="flex-1 h-9 sm:h-10 text-xs sm:text-sm bg-blue-600 hover:bg-blue-700"
                    >
                      {updating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Mark Deposit Paid
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDecline}
                      disabled={updating}
                      className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    >
                      {updating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Declining...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Decline Interest
                        </>
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={handleSaveNotes}
                    disabled={updating}
                    className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                  >
                    Save Notes Only
                  </Button>
                </div>
              )}

              {/* ACTION BUTTONS - DEPOSIT_PAID STATUS */}
              {selectedInterest.status === 'DEPOSIT_PAID' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleDecline}
                    disabled={updating}
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                  >
                    {updating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Declining...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Decline
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={updating}
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm bg-green-600 hover:bg-green-700"
                  >
                    {updating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve & Reserve Shares
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* CLOSE BUTTON - CONFIRMED/DECLINED */}
              {(selectedInterest.status === 'CONFIRMED' || selectedInterest.status === 'DECLINED') && (
                <Button
                  variant="outline"
                  onClick={() => setSelectedInterest(null)}
                  className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                >
                  Close
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}